import { useEffect, useState } from "react";
import { useConvexAuth } from "convex/react";
import { onAutoSaveChange } from "@/lib/snapshotSync";

/**
 * A persistent, unmissable banner shown whenever auto-save is off.
 *
 * App.tsx disables auto-save on a failed hydrate or an unresolved identity.
 * Both are correct — writing in either state can overwrite her real server
 * snapshot with a blank one. But the app stayed fully interactive and every
 * store write kept appearing to succeed, while the only notice was a one-time
 * toast phrased as a LOAD problem ("your data is safe"). She could log three
 * meals and a workout over twenty minutes, close the tab, and lose all of it —
 * having been told the opposite.
 *
 * A toast is the wrong shape for a condition that persists. This stays on
 * screen for exactly as long as the danger does, and says plainly what will
 * happen rather than what went wrong.
 *
 * Deliberately NOT dismissible: dismissing would hide an ongoing risk, and the
 * only real fix is a reload.
 *
 * It is also gated on being signed in. `autoSaveOn` starts false and only flips
 * true after a successful hydrate, so before sign-in this banner rendered on the
 * sign-in screen itself — telling a first-time visitor that we "couldn't reach
 * your account" and that her changes would be lost, before she had an account or
 * had typed anything. The warning is about losing work in progress; signed out
 * there is no work and no account, so there is nothing to warn about. Crying
 * wolf on the first screen is also how a real warning later gets ignored.
 */
export default function NotSavingBanner() {
  const [saving, setSaving] = useState(true);
  const { isLoading, isAuthenticated } = useConvexAuth();

  useEffect(() => onAutoSaveChange(setSaving), []);

  // No session means no snapshot to protect. Stay silent while auth resolves
  // too, so the banner cannot flash during the initial load.
  if (isLoading || !isAuthenticated) return null;

  if (saving) return null;

  return (
    <div
      role="alert"
      data-testid="banner-not-saving"
      className="fixed left-0 right-0 z-50 px-4 py-2.5"
      style={{ top: 0, background: "#C0784A", color: "#fff" }}
    >
      <p className="text-xs mnf-body" style={{ lineHeight: 1.35 }}>
        <strong>Your changes aren&rsquo;t being saved right now.</strong> We
        couldn&rsquo;t reach your account, so we&rsquo;ve paused saving to protect
        what&rsquo;s already stored. Anything you add until you reload will be lost.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="text-xs mnf-body mt-1 underline"
        style={{ color: "#fff" }}
        data-testid="button-reload-to-save"
      >
        Reload to start saving again
      </button>
    </div>
  );
}
